import { makeScreensGraph } from '@util/FlowUtils';
import React, { ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { PathProgress } from 'ts-utils';
import { FlowConfig } from '../../config/FlowTypes';
import { useAppStore } from '../../store/Store';
import { resolveNextScreen } from '../flows/FlowNavigation';
import { Header } from './Header';
import { Progress } from './Progress';

type Props<ScreenNames extends string> = {
  showProgress: boolean;
  config: FlowConfig<ScreenNames>;
  currentScreen: ScreenNames;
};

export const ProgressHeader = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { screens } = props.config;
  const flowGraph = useMemo(() => makeScreensGraph(screens), [screens]);
  const screenConfig = screens.find((screen) => screen.screenName === props.currentScreen);
  const nextScreen = useAppStore((state) =>
    screenConfig?.progressFollowsRoute === true ? resolveNextScreen(screenConfig, state) : undefined
  );

  /**
   * Calculates the progress of the current screen through all screens using a graph.
   * @returns Progress as a decimal between `0` and `1`, or undefined.
   */
  const progress = useMemo<number | undefined>(
    function calculateProgress() {
      const first = screens[0].screenName;
      if (nextScreen !== undefined) {
        // One step behind the screen the user is routed to, rather than the longest branch from here.
        const next = flowGraph.progress({ first, position: nextScreen });
        return displayValueForProgress({ ...next, progress: next.progress - 1 });
      }
      return displayValueForProgress(flowGraph.progress({ first, position: props.currentScreen }));
    },
    [flowGraph, screens, props.currentScreen, nextScreen]
  );

  /** +1 to show non-zero progress on first screen (e.g. step 0 of 4 would be 0%, but adding 1, step 1 of 5 is 20%) */
  function displayValueForProgress(progress: PathProgress | undefined): number | undefined {
    return progress !== undefined ? (progress.progress + 1) / (progress.total + 1) : undefined;
  }

  return (
    <Header>
      {props.showProgress && progress !== undefined && (
        <ProgressWrapper>
          <Progress percent={progress * 100} showInfo={false} strokeWidth={5} />
        </ProgressWrapper>
      )}
    </Header>
  );
};

const ProgressWrapper = styled.div`
  width: 100%;
  max-width: 100%;
`;
